import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useShipmentsList } from "../../../../../../features/shipments/queries";
import {
  isShipmentEligibleForTrip,
  type Shipment,
} from "../../../../../../features/shipments/types";
import type { ThemeClasses } from "../types";

interface TripShipmentsSectionProps {
  value?: string[];
  onChange: (shipmentIds: string[]) => void;
  themeClasses: ThemeClasses;
  t: TFunction;
  tripId?: string | null;
}

const PAGE_SIZE = 100;

export function TripShipmentsSection({
  value,
  onChange,
  themeClasses,
  t,
  tripId,
}: TripShipmentsSectionProps) {
  const [search, setSearch] = useState("");
  const [seeded, setSeeded] = useState(false);

  const eligibleQuery = useShipmentsList({
    page: 1,
    limit: PAGE_SIZE,
    without_trip: true,
  });

  const linkedQuery = useShipmentsList(
    { page: 1, limit: PAGE_SIZE, trip_id: tripId ?? undefined },
    { enabled: !!tripId },
  );

  const selected = useMemo(() => value ?? [], [value]);

  useEffect(() => {
    if (seeded || !tripId || !linkedQuery.data) return;
    setSeeded(true);
    onChange(linkedQuery.data.data.map((shipment) => shipment.id));
  }, [seeded, tripId, linkedQuery.data, onChange]);

  const shipments = useMemo(() => {
    const linked = linkedQuery.data?.data ?? [];
    const eligible = (eligibleQuery.data?.data ?? []).filter(
      isShipmentEligibleForTrip,
    );
    const byId = new Map<string, Shipment>();
    [...linked, ...eligible].forEach((shipment) => byId.set(shipment.id, shipment));
    return [...byId.values()];
  }, [linkedQuery.data, eligibleQuery.data]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return shipments;
    return shipments.filter(
      (shipment) =>
        shipment.tracking_number.toLowerCase().includes(term) ||
        shipment.shipment_name.toLowerCase().includes(term) ||
        shipment.client_name.toLowerCase().includes(term),
    );
  }, [shipments, search]);

  const toggle = (shipmentId: string) =>
    onChange(
      selected.includes(shipmentId)
        ? selected.filter((id) => id !== shipmentId)
        : [...selected, shipmentId],
    );

  const loading = eligibleQuery.isLoading || (!!tripId && linkedQuery.isLoading);

  return (
    <>
      <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
        {t("trips.shipments.title")}
        <span className="ml-2 text-xs font-normal text-content-muted">
          {t("trips.shipments.selected", { count: selected.length })}
        </span>
      </h3>

      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("trips.shipments.searchPlaceholder")}
          className={`w-full min-h-10 pl-9 pr-3 py-2 text-xs bg-app border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
        />
      </div>

      <div
        className={`max-h-56 overflow-y-auto rounded-lg border ${themeClasses.cardBorder} bg-surface p-2 space-y-1.5`}
      >
        {loading && (
          <div className="flex items-center justify-center py-6">
            <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
          </div>
        )}

        {!loading && visible.length === 0 && (
          <p className="px-2 py-3 text-xs text-content-muted">
            {t("trips.shipments.empty")}
          </p>
        )}

        {!loading &&
          visible.map((shipment) => {
            const checked = selected.includes(shipment.id);
            return (
              <button
                key={shipment.id}
                type="button"
                onClick={() => toggle(shipment.id)}
                className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                  checked
                    ? "border-primary-500 bg-primary-500/10"
                    : `${themeClasses.cardBorder} bg-card hover:bg-subtle`
                }`}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    checked
                      ? "border-primary-500 bg-primary-500 text-on-primary"
                      : "border-line"
                  }`}
                >
                  {checked && <i className="ri-check-line text-[10px]" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block truncate text-xs font-semibold ${themeClasses.text}`}>
                    {shipment.tracking_number || shipment.shipment_name}
                  </span>
                  <span className="block truncate text-[11px] text-content-muted">
                    {shipment.client_name}
                    {shipment.destination.address
                      ? ` · ${shipment.destination.address}`
                      : ""}
                  </span>
                </span>
              </button>
            );
          })}
      </div>
    </>
  );
}
